import { useMemo, type ReactNode } from 'react';
import { FileViewerProvider } from '@repletoryreach/sdk-ui/file-viewer';
import type { FileViewerLabels } from '@repletoryreach/sdk-ui/file-viewer';
import { useT, type Translate } from '../i18n/I18nProvider';

/**
 * Every string the file viewer renders, from this console's catalogue.
 *
 * The package defaults to English so that no copy needing translation lives
 * outside `src/i18n/catalog.ts`; this is where the catalogue meets the
 * component, so no page passes labels itself.
 */
export function fileViewerLabels(t: Translate): FileViewerLabels {
  return {
    close: t('fileViewer.close'),
    back: t('fileViewer.back'),
    download: t('fileViewer.download'),
    downloadOriginal: t('fileViewer.downloadOriginal'),
    print: t('fileViewer.print'),
    share: t('fileViewer.share'),
    copyLink: t('fileViewer.copyLink'),
    copyName: t('fileViewer.copyName'),
    copied: t('fileViewer.copied'),
    copyFailed: t('fileViewer.copyFailed'),
    openExternally: t('fileViewer.openExternally'),
    refresh: t('fileViewer.refresh'),
    refreshed: t('fileViewer.refreshed'),
    refreshedNewer: t('fileViewer.refreshedNewer'),
    details: t('fileViewer.details'),
    hideDetails: t('fileViewer.hideDetails'),
    fullscreen: t('fileViewer.fullscreen'),
    exitFullscreen: t('fileViewer.exitFullscreen'),
    keyboardHelp: t('fileViewer.keyboardHelp'),
    more: t('fileViewer.more'),
    previous: t('fileViewer.previous'),
    next: t('fileViewer.next'),
    position: t('fileViewer.position'),
    local: t('fileViewer.local'),
    remote: t('fileViewer.remote'),
    readOnly: t('fileViewer.readOnly'),
    retry: t('fileViewer.retry'),
    cancel: t('fileViewer.cancel'),
    loadedState: t('fileViewer.loadedState'),
    loadingState: t('fileViewer.loadingState'),
    inspectingState: t('fileViewer.inspectingState'),
    processingState: t('fileViewer.processingState'),
    errorState: t('fileViewer.errorState'),
    progressOf: t('fileViewer.progressOf'),
    progressLoaded: t('fileViewer.progressLoaded'),
    viewerTitle: t('fileViewer.viewerTitle'),
    untitled: t('fileViewer.untitled'),
    familyPdf: t('fileViewer.familyPdf'),
    familyImage: t('fileViewer.familyImage'),
    familyVideo: t('fileViewer.familyVideo'),
    familyAudio: t('fileViewer.familyAudio'),
    familySpreadsheet: t('fileViewer.familySpreadsheet'),
    familyPresentation: t('fileViewer.familyPresentation'),
    familyDocument: t('fileViewer.familyDocument'),
    familyCode: t('fileViewer.familyCode'),
    familyText: t('fileViewer.familyText'),
    familyArchive: t('fileViewer.familyArchive'),
    familyUnknown: t('fileViewer.familyUnknown'),
    detailFilename: t('fileViewer.detailFilename'),
    detailTitle: t('fileViewer.detailTitle'),
    detailType: t('fileViewer.detailType'),
    detailMime: t('fileViewer.detailMime'),
    detailSize: t('fileViewer.detailSize'),
    detailSource: t('fileViewer.detailSource'),
    detailHost: t('fileViewer.detailHost'),
    detailModified: t('fileViewer.detailModified'),
    detailEtag: t('fileViewer.detailEtag'),
    detailChecksum: t('fileViewer.detailChecksum'),
    computeChecksum: t('fileViewer.computeChecksum'),
    computingChecksum: t('fileViewer.computingChecksum'),
    detailDimensions: t('fileViewer.detailDimensions'),
    detailAspect: t('fileViewer.detailAspect'),
    detailDuration: t('fileViewer.detailDuration'),
    detailResolution: t('fileViewer.detailResolution'),
    detailLines: t('fileViewer.detailLines'),
    detailEncoding: t('fileViewer.detailEncoding'),
    detailLanguage: t('fileViewer.detailLanguage'),
    detailValidation: t('fileViewer.detailValidation'),
    detailFrames: t('fileViewer.detailFrames'),
    detailAlpha: t('fileViewer.detailAlpha'),
    detailRenderer: t('fileViewer.detailRenderer'),
    rendererNative: t('fileViewer.rendererNative'),
    rendererConverted: t('fileViewer.rendererConverted'),
    rendererNone: t('fileViewer.rendererNone'),
    yes: t('fileViewer.yes'),
    no: t('fileViewer.no'),
    unknownValue: t('fileViewer.unknownValue'),
    errorNetwork: t('fileViewer.errorNetwork'),
    errorCors: t('fileViewer.errorCors'),
    errorHttp: t('fileViewer.errorHttp'),
    errorAuth: t('fileViewer.errorAuth'),
    errorNotFound: t('fileViewer.errorNotFound'),
    errorExpired: t('fileViewer.errorExpired'),
    errorTooLarge: t('fileViewer.errorTooLarge'),
    errorUnsupported: t('fileViewer.errorUnsupported'),
    errorDecode: t('fileViewer.errorDecode'),
    errorAborted: t('fileViewer.errorAborted'),
    mismatchWarning: t('fileViewer.mismatchWarning'),
    unsupportedTitle: t('fileViewer.unsupportedTitle'),
    unsupportedSpreadsheet: t('fileViewer.unsupportedSpreadsheet'),
    unsupportedPresentation: t('fileViewer.unsupportedPresentation'),
    unsupportedDocument: t('fileViewer.unsupportedDocument'),
    unsupportedArchive: t('fileViewer.unsupportedArchive'),
    unsupportedUnknown: t('fileViewer.unsupportedUnknown'),
    unsupportedPdfInline: t('fileViewer.unsupportedPdfInline'),
    unsupportedMediaCodec: t('fileViewer.unsupportedMediaCodec'),
    unsupportedHls: t('fileViewer.unsupportedHls'),
    convertedPreview: t('fileViewer.convertedPreview'),
    convertedPreviewHint: t('fileViewer.convertedPreviewHint'),
    loadConvertedPreview: t('fileViewer.loadConvertedPreview'),
    convertedPreviewFailed: t('fileViewer.convertedPreviewFailed'),
    hexPreview: t('fileViewer.hexPreview'),
    hexPreviewHint: t('fileViewer.hexPreviewHint'),
    showHexPreview: t('fileViewer.showHexPreview'),
    originalKept: t('fileViewer.originalKept'),
    imageFit: t('fileViewer.imageFit'),
    imageFill: t('fileViewer.imageFill'),
    imageActual: t('fileViewer.imageActual'),
    zoomIn: t('fileViewer.zoomIn'),
    zoomOut: t('fileViewer.zoomOut'),
    rotateLeft: t('fileViewer.rotateLeft'),
    rotateRight: t('fileViewer.rotateRight'),
    flipHorizontal: t('fileViewer.flipHorizontal'),
    flipVertical: t('fileViewer.flipVertical'),
    resetView: t('fileViewer.resetView'),
    checkerboard: t('fileViewer.checkerboard'),
    transformNote: t('fileViewer.transformNote'),
    copyImage: t('fileViewer.copyImage'),
    exportPng: t('fileViewer.exportPng'),
    animatedNote: t('fileViewer.animatedNote'),
    svgNote: t('fileViewer.svgNote'),
    playbackSpeed: t('fileViewer.playbackSpeed'),
    loop: t('fileViewer.loop'),
    pictureInPicture: t('fileViewer.pictureInPicture'),
    fitContain: t('fileViewer.fitContain'),
    fitCover: t('fileViewer.fitCover'),
    fitOriginal: t('fileViewer.fitOriginal'),
    captureFrame: t('fileViewer.captureFrame'),
    quality: t('fileViewer.quality'),
    qualityAuto: t('fileViewer.qualityAuto'),
    live: t('fileViewer.live'),
    bufferHealth: t('fileViewer.bufferHealth'),
    droppedFrames: t('fileViewer.droppedFrames'),
    bandwidth: t('fileViewer.bandwidth'),
    protocol: t('fileViewer.protocol'),
    hlsNative: t('fileViewer.hlsNative'),
    hlsPlayer: t('fileViewer.hlsPlayer'),
    mediaDiagnostics: t('fileViewer.mediaDiagnostics'),
    mediaPlaybackFailed: t('fileViewer.mediaPlaybackFailed'),
    seekBack: t('fileViewer.seekBack'),
    seekForward: t('fileViewer.seekForward'),
    playPause: t('fileViewer.playPause'),
    wrapLines: t('fileViewer.wrapLines'),
    lineNumbers: t('fileViewer.lineNumbers'),
    searchInFile: t('fileViewer.searchInFile'),
    searchMatches: t('fileViewer.searchMatches'),
    searchNone: t('fileViewer.searchNone'),
    goToLine: t('fileViewer.goToLine'),
    language: t('fileViewer.language'),
    viewRaw: t('fileViewer.viewRaw'),
    viewRendered: t('fileViewer.viewRendered'),
    viewGrid: t('fileViewer.viewGrid'),
    prettyPrint: t('fileViewer.prettyPrint'),
    validJson: t('fileViewer.validJson'),
    invalidJson: t('fileViewer.invalidJson'),
    copyCode: t('fileViewer.copyCode'),
    truncatedNote: t('fileViewer.truncatedNote'),
    sandboxNote: t('fileViewer.sandboxNote'),
    noRendererNote: t('fileViewer.noRendererNote'),
    gridSummary: t('fileViewer.gridSummary'),
    emptyText: t('fileViewer.emptyText'),
    pickerTitle: t('fileViewer.pickerTitle'),
    pickerFamilies: t('fileViewer.pickerFamilies'),
    pickerFromDevice: t('fileViewer.pickerFromDevice'),
    pickerDrop: t('fileViewer.pickerDrop'),
    pickerOr: t('fileViewer.pickerOr'),
    pickerChoose: t('fileViewer.pickerChoose'),
    pickerFromLink: t('fileViewer.pickerFromLink'),
    pickerUrl: t('fileViewer.pickerUrl'),
    pickerUrlPlaceholder: t('fileViewer.pickerUrlPlaceholder'),
    pickerOpenLink: t('fileViewer.pickerOpenLink'),
    pickerSendCredentials: t('fileViewer.pickerSendCredentials'),
    pickerSendCredentialsHint: t('fileViewer.pickerSendCredentialsHint'),
    pickerRemember: t('fileViewer.pickerRemember'),
    pickerRecentLocal: t('fileViewer.pickerRecentLocal'),
    pickerRecentLinks: t('fileViewer.pickerRecentLinks'),
    pickerName: t('fileViewer.pickerName'),
    pickerSize: t('fileViewer.pickerSize'),
    pickerModified: t('fileViewer.pickerModified'),
    pickerLink: t('fileViewer.pickerLink'),
    pickerLastOpened: t('fileViewer.pickerLastOpened'),
    pickerLocalNote: t('fileViewer.pickerLocalNote'),
    pickerRemoteNote: t('fileViewer.pickerRemoteNote'),
    pickerSizeNote: t('fileViewer.pickerSizeNote'),
    pickerReopenNote: t('fileViewer.pickerReopenNote'),
    pickerInvalidUrl: t('fileViewer.pickerInvalidUrl'),
    pickerNoRecent: t('fileViewer.pickerNoRecent'),
    pickerClearRecent: t('fileViewer.pickerClearRecent'),
    helpTitle: t('fileViewer.helpTitle'),
    helpEscape: t('fileViewer.helpEscape'),
    helpArrows: t('fileViewer.helpArrows'),
    helpZoom: t('fileViewer.helpZoom'),
    helpFit: t('fileViewer.helpFit'),
    helpActual: t('fileViewer.helpActual'),
    helpRotate: t('fileViewer.helpRotate'),
    helpSearch: t('fileViewer.helpSearch'),
    helpFullscreen: t('fileViewer.helpFullscreen'),
    helpDetails: t('fileViewer.helpDetails'),
    helpSpace: t('fileViewer.helpSpace'),
    helpSeek: t('fileViewer.helpSeek'),
  };
}

/**
 * Mount once, inside `I18nProvider`. Syntax colouring for the code family is
 * loaded on demand from the design system's own highlighter; nothing else
 * optional is bundled.
 */
export function FileViewerHost({ children }: { children: ReactNode }) {
  const t = useT();
  const labels = useMemo(() => fileViewerLabels(t), [t]);
  return (
    <FileViewerProvider
      labels={labels}
      loaders={{ highlight: () => import('@repletoryreach/sdk-ui/code-highlight') }}
    >
      {children}
    </FileViewerProvider>
  );
}
